import React, { useState, useEffect, useMemo } from "react";
import { Container, Row, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Flex, Radio, Badge, Space } from "antd";
import { ArrivalCard } from "./ArrivalCard";
import { BusStatusSection } from "./BusStatusSection";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Bus01Icon,
  FilterIcon,
  Search01Icon,
} from "@hugeicons/core-free-icons";
import { AntInput } from "../../../CommonElements/AntInput";
import BtnAsLink from "../../../Component/BtnAsLink";
import { useGetServiceCenterArrivalsQuery } from "../../../services/arrivals";
import { useMadinaDepartureCampaignsListQuery } from "@/services/Departure/departures";
import { useDebouncedSearch } from "../../../utils/hooks/useDebouncedSearch";
import ArrivalCardSkeleton from "./ArrivalCardSkeleton";
import { BusListModal } from "./BusListModal";
import { Button } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useArrival } from "./hooks/useArrival";
import { useFilter } from "../../../CommonElements/TableC/hooks/useFilter";
import { GenericFilterModal } from "../../../CommonElements/TableC/GenericFilterModal";
import { AntPagination } from "@/Component/AntPagination/AntPagination";
import { permissions } from "@/utils/constants";
import { useSystem } from "@/utils/hooks/useLocation";
import usePermissions from "@/utils/hooks/usePermissions";

const BusReceptionList = ({ port }) => {
  const { t } = useTranslation();
  const {
    filters,
    isFilterOpen,
    setIsFilterOpen,
    handleFilterApply,
    handleFilterReset,
  } = useFilter();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedArrival, setSelectedArrival] = useState(null);
  const [isBusModalOpen, setIsBusModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [dateQuery, setDateQuery] = useState(
    searchParams.get("date") || "today"
  );
  const { from_id } = useSystem();
  const {
    hasAnyPermission
  } = usePermissions();
  const source = searchParams.get("source") || "delegation";
  /** Legacy JED/ airport “nusuk” source only — not organizer between-cities */
  const isNusuk = source === "nusuk";
  const isBetweenCities = source === "between_cities";
  // Use debounced search hook
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const duration = dateQuery === "today" ? "today" : "all";

  const {
    data: serviceCenterRes,
    isLoading: serviceCenterLoading,
    isFetching: serviceCenterFetching,
  } = useGetServiceCenterArrivalsQuery(
    {
      port,
      search: debouncedValue,
      page: currentPage,
      filters,
      date: dateQuery,
    },
    { enabled: !isBetweenCities }
  );

  const {
    data: madinaDepartureRes,
    isLoading: madinaDepartureLoading,
    isFetching: madinaDepartureFetching,
  } = useMadinaDepartureCampaignsListQuery(
    {
      duration,
      page: currentPage,
      per_page: 10,
      search: debouncedValue,
      filters,
    },
    { enabled: isBetweenCities }
  );

  const arrivalsRes = isBetweenCities ? madinaDepartureRes : serviceCenterRes;
  const isLoading = isBetweenCities ? madinaDepartureLoading : serviceCenterLoading;
  const isFetching = isBetweenCities ? madinaDepartureFetching : serviceCenterFetching;

  const arrivals = useMemo(() => {
    if (!isBetweenCities) {
      return serviceCenterRes?.data?.data || [];
    }
    const raw = madinaDepartureRes?.data?.data || [];
    return raw.flatMap((item) =>
      (item?.departure_details ?? []).map((departure) => ({
        ...item.arrival_campaign,
        ...departure,
        type: item.arrival_campaign?.type ?? "campaign",
      }))
    );
  }, [isBetweenCities, serviceCenterRes, madinaDepartureRes]);

  const meta = arrivalsRes?.data?.meta || {};
  const { labelValues, filterFields, title } = useArrival(
    arrivalsRes?.data?.date,
    { port }
  );
  const loading = isLoading || isFetching;

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Update selectedArrival when arrivals data changes (after mutations).
  // Match id + type: campaign and group rows can share the same numeric id.
  useEffect(() => {
    if (selectedArrival && arrivals.length > 0) {
      const updatedArrival = arrivals.find(
        (arrival) =>
          arrival.id === selectedArrival.id &&
          (arrival?.type ?? "") === (selectedArrival?.type ?? "")
      );
      if (updatedArrival) {
        setSelectedArrival(updatedArrival);
      }
    }
  }, [arrivals, selectedArrival]);

  const getDepartureListStatusConfig = (item) => {
    switch (item.status) {
      case "scheduled":
        return {
          color: "brand",
          icon: Bus01Icon,
          text: <Space>{t("DEPARTURE_CANCELLED")}</Space>,
        };
      case "departed":
        return {
          color: "warning",
          icon: Bus01Icon,
          text: <Space>{t("DEPARTURE_COMPLETED")}</Space>,
        };
      case "arrived":
        return {
          color: "success",
          icon: Bus01Icon,
          text: <Space>{t("DEPARTURE_ARRIVED")}</Space>,
        };
      default:
        return {
          color: "gray",
          icon: Bus01Icon,
          text: <Space>{t("DEPARTURE_SCHEDULED")}</Space>,
        };
    }
  };

  // Helper function to determine arrival status based on bus_cards
  const getArrivalStatus = (busCards) => {
    if (isNusuk && !busCards) {
      return {
        status: "complete_arrival",
        color: "success",
        icon: Bus01Icon,
      };
    }
    if (!busCards || busCards.length === 0) {
      return {
        status: "no_buses",
        color: "danger", // Changed from 'brand' to 'danger'
        text: t("NO_BUSES_FOUND"), // Changed from STATUS_ARRIVING_TO_CENTER
        icon: Bus01Icon,
        arrivedCount: 0,
        totalCount: 0,
      };
    }

    const totalBuses = busCards.length;
    const arrivedBuses = busCards.filter(
      (bus) => bus.status === "arrived_hotel"
    ).length;

    if (arrivedBuses === 0) {
      // All buses not arrived yet
      return {
        status: "arriving_to_center",
        color: "brand",
        text: t("STATUS_ARRIVING_TO_CENTER"),
        icon: Bus01Icon,
        arrivedCount: 0,
        totalCount: totalBuses,
      };
    } else if (arrivedBuses === totalBuses) {
      // All buses arrived
      return {
        status: "complete_arrival",
        color: "success",
        text: t("STATUS_COMPLETE_ARRIVAL"),
        icon: Bus01Icon,
        arrivedCount: arrivedBuses,
        totalCount: totalBuses,
      };
    } else {
      // Partial arrival
      return {
        status: "partial_arrival",
        color: "success", // Changed from 'warning' to 'success'
        text: t("STATUS_PARTIAL_ARRIVAL"),
        icon: Bus01Icon,
        arrivedCount: arrivedBuses,
        totalCount: totalBuses,
      };
    }
  };

  // Status mapping for center arrivals based on bus_cards (or departure segment status)
  const getStatusConfig = (arrival) => {
    if (isBetweenCities && arrival?.status) {
      return getDepartureListStatusConfig(arrival);
    }
    return getArrivalStatus(arrival.bus_cards);
  };

  // Create action button for viewing buses
  const createActions = (arrival) => {
    if (isBetweenCities) {
      if (!hasAnyPermission([permissions.control_center_reception_cards_between_cities])) {
        return null;
      }
    } else if (!hasAnyPermission([permissions.control_center_reception_cards_from_ports])) {
      return null;
    }
    const viewBusesButton = (
      <Button
        className="btn-primary flex-fill"
        onClick={() => {
          setSelectedArrival(arrival);
          setIsBusModalOpen(true);
        }}
      >
        {t("VIEW_BUSES")}
      </Button>
    );

    return <>{viewBusesButton}</>;
  };

  // Create skeleton items for loading state
  const skeletonItems = Array.from({ length: 3 }, (_, index) => ({
    id: index,
  }));

  const additionalFilterFields = useMemo(() => {
    const fields = [...filterFields];
    if (!isBetweenCities) {
      fields.push({
        name: "company_name",
        label: t("TRANSPORT_COMPANY"),
        type: "select",
        optionKey: "buses",
        options: (company) => ({
          value: company,
          label: company,
        }),
      });
    }
    return fields;
  }, [filterFields, isBetweenCities, t]);
  const handleDateChange = (e) => {
    setDateQuery(e.target.value);
  };
  const options = [
    { label: t("TODAY"), value: "today" },
    { label: t("ALL"), value: "all" },
  ];
  return (
    <Container fluid>
      <Stack gap={3}>
        <Stack direction="horizontal" gap={2}>
          <div className="flex-fill">
            <AntInput
              prefix={<HugeiconsIcon icon={Search01Icon} />}
              type="text"
              placeholder={t("SEARCH_HERE")}
              value={searchValue}
              onChange={handleSearchChange}
              disabled={isFetching}
            />
          </div>
          <Flex gap={8}>
            <Badge
              count={Object.values(filters || {})?.filter?.(Boolean)?.length}
            >
              <BtnAsLink
                className="btn-secondary"
                icon={<HugeiconsIcon icon={FilterIcon} />}
                onClick={() => setIsFilterOpen(true)}
              >
                {t("FILTER")}
              </BtnAsLink>
            </Badge>
          </Flex>
        </Stack>

        <Flex justify="flex-start">
          {(isNusuk || isBetweenCities) && (
            <Radio.Group
              disabled={loading}
              onChange={handleDateChange}
              value={dateQuery}
              options={options}
            />
          )}
        </Flex>

        {isNusuk && !arrivals.length && !loading && (
          <div className="text-center py-5">
            <HugeiconsIcon size={40} icon={Bus01Icon} />
            <h4 className="text-muted mb-2">{t("NO_ARRIVALS_FOUND")}</h4>
            <p className="text-muted mb-0">{t("NO_ARRIVALS_DESCRIPTION")}</p>
          </div>
        )}

        <Row className="flex-wrap">
          {loading ? (
            skeletonItems.map((item) => <ArrivalCardSkeleton key={item.id} />)
          ) : arrivals.length === 0 ? (
            <div className="text-center py-5">
              <HugeiconsIcon size={40} icon={Bus01Icon} />
              <h4 className="text-muted mb-2">
                {debouncedValue
                  ? t("NO_SEARCH_RESULTS") || "لا توجد نتائج للبحث"
                  : t("NO_ARRIVALS_FOUND") || "لا يوجد وصول"}
              </h4>
              <p className="text-muted mb-0">
                {debouncedValue
                  ? t("NO_SEARCH_RESULTS_DESCRIPTION") ||
                  "لم يتم العثور على وصولات تطابق البحث"
                  : t("NO_ARRIVALS_DESCRIPTION") || "لم يتم العثور على أي وصول"}
              </p>
            </div>
          ) : (
            arrivals.map((arrival) => (
              <ArrivalCard
                title={
                  isBetweenCities
                    ? t("CAMPAIGN_NUMBER")
                    : isNusuk
                      ? title[source]
                      : title[arrival?.type]
                }
                titleValue={
                  isBetweenCities
                    ? arrival?.campaign_id ??
                      arrival?.campaign_number ??
                      arrival?.serial ??
                      "-"
                    : arrival?.report_number ??
                      arrival?.request_number ??
                      arrival?.serial ??
                      "-"
                }
                groupName={arrival?.name}
                key={arrival.id}
                arrival={arrival}
                date={arrivalsRes?.data?.date}
                statusConfig={getStatusConfig(arrival)}
                actions={isNusuk ? undefined : createActions(arrival)}
                labelValues={labelValues}
                extra={
                  isNusuk ? (
                    <></>
                  ) : (
                    <BusStatusSection
                      label="ARRIVAL_CARD_BUS_ARRIVAL_STATUS"
                      count={getStatusConfig(arrival).arrivedCount}
                      totalCount={getStatusConfig(arrival).totalCount}
                    />
                  )
                }
              />
            ))
          )}
        </Row>

        <AntPagination
          className="sticky-bottom z-1 w-100 p-3 bg-white"
          hideOnSinglePage
          pageSize={meta.per_page || 10}
          current={currentPage}
          onChange={handlePageChange}
          total={meta.total || 0}
          showSizeChanger={false}
        />
      </Stack>

      {/* Bus List Modal */}
      {selectedArrival && (
        <BusListModal
          isOpen={isBusModalOpen}
          toggle={() => {
            setIsBusModalOpen(false);
            setSelectedArrival(null);
          }}
          busCards={selectedArrival.bus_cards || []}
          arrivalId={selectedArrival.id}
        />
      )}
      <GenericFilterModal
        isOpen={isFilterOpen}
        toggle={() => setIsFilterOpen(false)}
        onApply={handleFilterApply(() => {
          setCurrentPage(1);
        })}
        onReset={handleFilterReset(() => {
          setCurrentPage(1);
        })}
        filterFields={additionalFilterFields}
        filters={filters}
      />
    </Container>
  );
};

export default BusReceptionList;
